import {forkJoin, Observable, of} from 'rxjs';
import {inject, Injectable} from '@angular/core';
import {BaseService} from '@shared/services/api/base-service';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {map, mergeMap} from 'rxjs/operators';
import {EquipmentResponse} from '../collectors-equipment/equipment.types';
import {SiteService} from '@shared/services';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {Collector, CollectorType, Device, Equipment, Level, MetadataType, VirtualFile} from '@app/graphql-typings/graphql';
import {CommissionDataNavigationState} from "@app/layout/sitemanager/types/types";
import {RoutingStateService} from "@shared/services/routing/routing-state.service";
import {SiteInfo} from "@shared/services/api/access-sites.interface";
import {NavigationCacheService} from "@app/layout/sitemanager/services/navigation-cache.service";
import {StringUtils} from '@shared/util/general/string-utils';


export const collectorEquipmentCommissioningResolver: ResolveFn<EquipmentResponse> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<EquipmentResponse> => {
  const navigationState: CommissionDataNavigationState = inject(RoutingStateService).getNavigationState<CommissionDataNavigationState>();
  const collectorEquipmentCommissioningService: CollectorEquipmentCommissioningService = inject(CollectorEquipmentCommissioningService);
  const siteId: number = StringUtils.stringToNumber(route.paramMap.get('site-id'));
  const collectorType: CollectorType = route.data.collector_type;
  return collectorEquipmentCommissioningService.getEquipmentTableData(siteId, collectorType, navigationState);
};

@Injectable({
  providedIn: 'root'
})
export class CollectorEquipmentCommissioningService extends BaseService {
  private readonly logger: AceLogger;

  constructor(private siteService: SiteService,
              private navigationCacheService: NavigationCacheService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(CollectorEquipmentCommissioningService.name);
  }

  getEquipmentTableData(siteId, collectorType: CollectorType, navigationState: CommissionDataNavigationState): Observable<EquipmentResponse> {
    this.logger.debug('getEquipmentTableData', siteId, collectorType);
    const collector$ = navigationState?.collector
      ? of((navigationState.collector))
      : this.navigationCacheService.getSiteCollector(siteId, collectorType);
    return collector$.pipe(mergeMap((collector: Collector) => {
      const equipment$ = navigationState?.equipment
        ? of(navigationState?.equipment)
        : this.navigationCacheService.getEquipment(collector.collector_id, collectorType);
      const metadataTypes$ = navigationState?.metadataTypes
        ? of(navigationState?.metadataTypes)
        : this.navigationCacheService.getMetadataTypes();
      const devices$ = collectorType === CollectorType.Physical
        ? (navigationState?.devices ? of(navigationState?.devices) : this.navigationCacheService.getDevices(collector.collector_id))
        : of(null);
      const files$ = collectorType === CollectorType.Virtual
        ? (navigationState?.files ? of(navigationState?.files) : this.navigationCacheService.getFiles(siteId))
        : of(null);
      const levels$ = navigationState?.levels
        ? of(navigationState?.levels)
        : this.navigationCacheService.getSiteLevels(siteId);
      return forkJoin([
        this.siteService.getCachedSiteInfo(siteId),
        equipment$,
        metadataTypes$,
        devices$,
        files$,
        levels$])
        .pipe(map(([site, collectorEquipment, metadataTypes, devices, files, levels]: [SiteInfo, Equipment[], MetadataType[], Device[], VirtualFile[], Level[]]) => {
          return {site, collector, collectorEquipment, metadataTypes, devices, files, levels};
        }));
    }));
  }

}
